import React, { useEffect, createContext } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { getUserDetails } from 'src/api/GraphServiceApi';
import * as actionTypes from 'src/actions';
import { store } from 'src/App';
import { rejectUserAuth, showToast } from 'src/actions';
import { msalInstance, ensureMsalReady, loginRequest } from './msalConfig';

export const defaultCurrentWeek = {
	startDate: moment().startOf('day').format('YYYY-MM-DD HH:mm'),
	endDate: moment().startOf('day').add(7, 'day').format('YYYY-MM-DD HH:mm')
};

const isInteractionRequired = (error) => {
	if (!error.message || error.message.length <= 0) return false;
	return (
		error.message.indexOf('consent_required') > -1 ||
		error.message.indexOf('interaction_required') > -1 ||
		error.message.indexOf('login_required') > -1 ||
		error.message.indexOf('no_account_in_silent_request') > -1
	);
};

let interactionPromise = null;

const getAccessToken = async () => {
	await ensureMsalReady();

	try {
		const accounts = msalInstance.getAllAccounts();
		if (accounts.length <= 0) throw new Error('login_required');

		const silentResult = await msalInstance.acquireTokenSilent({
			scopes: loginRequest.scopes,
			account: accounts[0]
		});
		localStorage.setItem('accessMicrosoftToken', silentResult.accessToken);
		return silentResult.accessToken;
	} catch (error) {
		if (isInteractionRequired(error)) {
			if (interactionPromise) return interactionPromise;

			interactionPromise = msalInstance
				.acquireTokenPopup({ scopes: loginRequest.scopes })
				.then((result) => {
					localStorage.setItem('accessMicrosoftToken', result.accessToken);
					return result.accessToken;
				})
				.catch((e) => {
					store.dispatch(showToast({ body: e.message, type: 'error' }));
					throw e;
				})
				.finally(() => {
					interactionPromise = null;
				});

			return interactionPromise;
		}
		throw error;
	}
};

const getUserProfile = async () => {
	try {
		if (store.getState().outlook.rejectUserAuth) {
			return { user: null, accessToken: null };
		}
		const accessToken = await getAccessToken();
		const user = await getUserDetails(accessToken);
		return { user, accessToken };
	} catch (error) {
		if (error.errorCode === 'user_cancelled') {
			store.dispatch(rejectUserAuth(true));
		}
		return null;
	}
};

const login = () => async (dispatch) => {
	try {
		await ensureMsalReady();
		await msalInstance.loginPopup({
			scopes: loginRequest.scopes,
			prompt: 'select_account'
		});
		await getAccessToken();
		await dispatch(rejectUserAuth(false));
		await dispatch(actionTypes.setMicrosoftLogin(true));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Successfully login' }));
	} catch (error) {
		await dispatch(
			actionTypes.showToast({ type: 'error', body: error?.message || 'An error has occured' })
		);
	}
};

const logout = () => {
	msalInstance.logoutPopup();
};

export const ContextProvider = createContext();

function AzureProvider({ children }) {
	useEffect(() => {
		ensureMsalReady().then(() => {
			const accounts = msalInstance.getAllAccounts();
			if (accounts && accounts.length > 0) {
				getAccessToken();
			}
		});
	}, []);

	return (
		<ContextProvider.Provider
			value={{
				login,
				logout,
				getAccessToken,
				getUserProfile
			}}
		>
			{children}
		</ContextProvider.Provider>
	);
}

AzureProvider.propTypes = {
	children: PropTypes.node
};

export default AzureProvider;
